import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MathJaxContext } from 'better-react-mathjax';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

import AboutSources from './AboutSections/AboutSources';
import AboutAttributes from './AboutSections/Attributes/AboutAttributes';
import AboutGeneTypes from './AboutSections/AboutGeneTypes';
import AboutVariantTypes from './AboutSections/Attributes/Variants/VariantTypes';
import AboutSchema from './AboutSections/AboutSchema';
import AboutMetrics from './AboutSections/Metrics/AboutMetrics';
import AboutOntology from './AboutSections/AboutOntology';
import AboutCiteUs from './AboutSections/AboutCiteUs';
import AboutSidebar from './components/elements/AboutSidebar';
import LoadingSpinner from '../../global/Loader/LoaderSpinner';

import StatisticsDiagram from './components/statistics/StatisticsDiagram';
import Changelog from './components/statistics/Changelog';
import { StatsTxt } from './components/statistics/StatisticsText';
import { StatsJSON, VersionsArray, VersionInfo } from './types';
import { StatisticsTable } from './components/statistics/StatisticsTables';
import { formatVersion, toHashLabel, compareVersions } from './helpers';
import { releasesApiService } from '../../services/releasesApiService';
import { apiReleaseToVersionsArray } from './releaseApiMappers';

function AboutContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statistics, setStats] = useState<StatsJSON | null>(null);
  const [apiVersions, setApiVersions] = useState<VersionsArray[]>([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const hasSection = searchParams.has('section');
    const hasVersion = searchParams.has('version');

    if (!hasSection && !hasVersion) {
      navigate(`${location.pathname}?section=gdaStatistics`, { replace: true });
    }
  }, [searchParams, location.pathname, navigate]);

  // Cargar stats.json (historial estático) y releases desde la API del backoffice (Releases manager)
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      let statsData: StatsJSON | null = null;

      try {
        const res = await fetch('/stats.json');
        if (res.ok) {
          statsData = (await res.json()) as StatsJSON;
        } else {
          statsData = { current: '' };
        }
      } catch (error) {
        console.error('❌ Error loading stats.json:', error);
        statsData = { current: '' };
      }

      if (cancelled) return;
      setStats(statsData);

      try {
        const list = await releasesApiService.getReleasesList();
        const versionKeysFromStats = new Set(Object.keys(statsData || {}).filter(k => k !== 'current'));
        const versionsToFetch = list.map(r => r.version).filter(v => !versionKeysFromStats.has(v));
        const details = await Promise.all(
          versionsToFetch.map(version => releasesApiService.getRelease(version)),
        );
        if (cancelled) return;
        const fromApi = details.map(apiReleaseToVersionsArray);
        setApiVersions(fromApi);
      } catch (error) {
        console.error('❌ Error loading releases from API:', error);
        setApiVersions([]);
      }

      if (!cancelled) setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !statistics) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const versionsFromStats: VersionsArray[] = Object.entries(statistics)
    .filter(([key]) => key !== 'current')
    .map(([key, value]) => {
      if (typeof value === 'string') return null;
      return { ...(value as VersionInfo), version: key };
    })
    .filter((v): v is VersionsArray => v !== null);

  const statsVersionSet = new Set(versionsFromStats.map(v => v.version));
  const apiOnly = apiVersions.filter(v => !statsVersionSet.has(v.version));
  const versionsArray: VersionsArray[] = [...versionsFromStats, ...apiOnly].sort((a, b) =>
    compareVersions(b.version as string, a.version as string),
  );

  // Versión "actual" para Statistics (gdaStatistics): la más reciente de la lista, para que
  // las releases subidas desde el backoffice actualicen también esta sección.
  const currentVersion = versionsArray.length > 0 ? versionsArray[0].version : statistics.current || '';
  const section = searchParams.get('section');
  const versionParam = searchParams.get('version');

  const activeVersionObj = (() => {
    if (versionParam) {
      return versionsArray.find(v => toHashLabel(v.version as string) === versionParam);
    }
    return versionsArray.find(v => v.version === currentVersion) ?? versionsArray[0] ?? null;
  })();

  const showVersionDetails = Boolean(versionParam && activeVersionObj);

  if (versionsArray.length === 0) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-gray-600">No version history data available.</p>
      </section>
    );
  }

  return (
    <MathJaxContext>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="sr-only">Information about database statistics, attributes and metrics.</h1>
        {/* Responsive sidebar */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors mt-4 lg:mt-0"
          >
            <span className="font-medium text-gray-900">Navigation Menu</span>
            {sidebarOpen ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-500" />
            )}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row lg:gap-8 border-b border-gray-200 pt-3 pb-4">
          {/* Mobile sidebar */}
          <div
            className={`w-full lg:w-1/4 lg:flex-shrink-0 mb-6 lg:mb-0 lg:block ${sidebarOpen ? 'block' : 'hidden'}`}
          >
            <div className="lg:sticky lg:top-4">
              <div className="lg:border-0 rounded-lg lg:rounded-none p-2 lg:p-0 bg-white lg:bg-transparent">
                <AboutSidebar versionsArray={versionsArray} onClick={() => setSidebarOpen(!sidebarOpen)} />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="w-full lg:w-3/4 lg:flex-grow">
            <div className="tab-content" id="dbInfoTabbedPane">
              {showVersionDetails ? (
                <div className="space-y-6">
                  {activeVersionObj.releaseDate && (
                    <h2 className="text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl">
                      {activeVersionObj.releaseDate}: Release of version{' '}
                      {formatVersion(activeVersionObj.version)} of DISGENET
                    </h2>
                  )}
                  {activeVersionObj && Object.keys(activeVersionObj.statistics).length > 0 && (
                    <StatsTxt version={activeVersionObj.version} statistics={activeVersionObj.statistics} />
                  )}
                  {activeVersionObj && (
                    <Changelog
                      version={activeVersionObj.version}
                      releaseDate={activeVersionObj.releaseDate}
                      statistics={activeVersionObj.statistics}
                      changelog={activeVersionObj.changelog}
                      currentVersion={currentVersion}
                    />
                  )}
                </div>
              ) : (
                <div className="space-y-6">
                  {section === 'gdaStatistics' && (
                    <div className="tab-pane active show" id="gdaStatistics">
                      <h2 className="text-xl font-bold text-gray-900 mb-4 sm:text-2xl sm:mb-6">Statistics</h2>
                      <div className="space-y-6 sm:space-y-8">
                        <StatsTxt
                          statistics={activeVersionObj!.statistics}
                          version={activeVersionObj!.version}
                        />
                        <StatisticsDiagram statistics={activeVersionObj!.statistics} />
                        <StatisticsTable />
                      </div>
                    </div>
                  )}

                  {section === 'gene_type' && (
                    <div className="tab-pane active show" id="gene_type">
                      <AboutGeneTypes />
                    </div>
                  )}

                  {section === 'variant_type' && (
                    <div className="tab-pane active show" id="variant_type">
                      <AboutVariantTypes />
                    </div>
                  )}

                  {section === 'schema' && (
                    <div className="tab-pane active show" id="schema">
                      <AboutSchema />
                    </div>
                  )}

                  {section === 'sources' && (
                    <div className="tab-pane active show" id="sources">
                      <AboutSources />
                    </div>
                  )}

                  {section === 'metrics' && (
                    <div className="tab-pane active show" id="metrics">
                      <AboutMetrics />
                    </div>
                  )}

                  {section === 'attributes' && (
                    <div className="tab-pane active show" id="attributes">
                      <AboutAttributes />
                    </div>
                  )}

                  {section === 'assoctypeont' && (
                    <div className="tab-pane active show" id="assoctypeont">
                      <AboutOntology />
                    </div>
                  )}

                  {section === 'cite_us' && (
                    <div className="tab-pane active show" id="cite_us">
                      <AboutCiteUs />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </MathJaxContext>
  );
}

export default AboutContent;
